import { useState, type ImgHTMLAttributes } from 'react'

import { storefrontImage } from '@/lib/storefrontImage'

export interface StoreImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Source path; mapped to the manifest's optimized AVIF when known. */
  src?: string | null
  /**
   * Rendered when the optimized image fails to load. Defaults to the
   * original source, so a missing AVIF still shows the product.
   */
  fallbackSrc?: string | null
}

/**
 * Every customer-facing product photo goes through here.
 *
 * The `<img>` loads the manifest's optimized static AVIF, remembers the
 * source it was given in `data-original-src` (the regression scripts
 * read it back), and falls back to the original source if the optimized
 * file ever fails. All other `<img>` props pass straight through.
 */
export function StoreImage({ src, fallbackSrc, onError, ...props }: StoreImageProps) {
  const original = src ?? undefined
  const optimized = original ? storefrontImage(original) : undefined
  const [failedSource, setFailedSource] = useState<string>()
  const failed = Boolean(optimized && failedSource === optimized)
  const resolved = failed ? fallbackSrc ?? original : optimized

  return (
    <img
      {...props}
      data-original-src={original}
      src={resolved}
      onError={(event) => {
        if (!failed) setFailedSource(optimized)
        onError?.(event)
      }}
    />
  )
}
